import { apiClient } from './client';
import { avatarImageUrl } from '../lib/avatar-src';

/**
 * Task API client (P3-TASK-FE) — backend `apps/chat-server/src/modules/tasks/{projects,tasks}.routes.ts` 와 1:1.
 *
 * 후속: yaml SSOT 정합 (현재 manual type — 다음 사이클).
 */

export type TaskStatus = 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'DONE';
export type TaskPriority = 'HIGH' | 'MEDIUM' | 'LOW';

export interface TaskProjectDto {
  id: string;
  createdBy: string;
  /**
   * 완료 승인 필요 — 켜면 [완료]가 «검토 요청»이 된다 (2026-10-12 · 정본 이관).
   *
   * ⚠ 서버가 이 칸을 응답에 싣는 자리는 projects.routes.ts 의 select 넷 ＋ 응답 펴기다.
   *   없으면 토글이 제 값을 못 읽어 «늘 꺼진 채로» 보인다 ⇒ 왕복 가드가 그 짝을 잠근다.
   */
  requiresDoneApproval?: boolean;
  name: string;
  description: string | null;
  dueDate: string | null;
  isArchived: boolean;
  taskCount: number;
  createdAt: string;
  // project-menu-detailing — 팀 표시 필드 (목록 응답 additive, 단건 응답엔 없을 수 있음).
  teamId?: string | null;
  teamName?: string | null;
  // review loop1 보류분 해소 — DONE 태스크 서버 집계 (목록 응답 additive, 단건 응답엔 없음).
  doneTaskCount?: number;
  // v2.9 — 제목 링크 (새 창, http(s)만). 없으면 null/undefined.
  linkUrl?: string | null;
  /**
   * ★★ 2026-10-04 — **받는** 쪽 두 칸 (조각 ⓒ · PR #2161 이 서버에 세웠다).
   *
   * ⛔ **이 둘이 없으면 서버가 내주는 값을 화면이 «타입으로 못 받는다»** — 그러면 만든 직후
   *   화면이 「내가 고른 색」을 못 그린다(새로고침 전까지 회색). 설계 문서가 그것을 **㉰ 의
   *   증상**이라 적었고, 2026-10-04 에 재서 그 칸이 **0건**임을 찾았다.
   *   근거: 서버 `projects.routes.ts:371`(select) · `:151-152`(DTO) 가 둘 다 내준다.
   *
   * ★ `color` 는 **null 일 수 있다** — 그때는 종전처럼 **id 해시 색**(`stripColor`)으로 그린다.
   *   (`stripColor` 는 main 에 이미 18건 쓰이고 있다 — 폴백은 «이미 선다».)
   * ⚠ `visibility` 는 **`string` 으로 둔다** — 좁히지 않는다. 표 CHECK 가 둘만 받지만,
   *   옛 응답이나 새 값이 올 때 **타입이 거짓말을 한다**. 서버 DTO 도 같은 까닭으로 `string` 이다
   *   (`projects.routes.ts:148-152` 의 그 주석) ⇒ **같은 뜻을 두 글자로** 두지 않는다.
   * ⇒ ★ 즉 **받는 쪽은 넓게 · 보내는 쪽은 좁게**(아래 `CreateProjectInput`).
   */
  color?: string | null;
  visibility?: string;
  /**
   * 2026-08-07 — 멤버 아바타 미리보기 (**ProjectMember 기준**, 목록 응답 additive).
   * 서버가 role→합류순 상위 5명만 담아 보낸다. `avatarUrl` 은 listProjects 에서
   * 표시용 URL 로 변환해 반환한다(컴포넌트는 <img src> 직결).
   * 구버전 서버 응답에는 없으므로 optional — 없으면 호출부가 기존 팀 기준 경로로 폴백한다.
   */
  memberPreview?: ProjectMemberPreviewDto[];
  /** preview 상한과 무관한 **총 멤버 수** — "+N" 표기용. */
  memberCount?: number;
  /**
   * ⭐ 2026-10-08 — **내 권한**. 서버가 `ProjectMember` 에서 내 행을 찾아 붙인다
   * (`projects.routes.ts:309-324` — 이미 읽는 행에 칸 하나라 ★ 추가 질의 0).
   *
   * ```
   * null       ⇒ 알고 있고 ★ «참여 안 했다»(관리자 우회 조회 등)
   * 'OWNER'…   ⇒ 그 역할 (prisma `enum ProjectRole` 의 넷과 ★ 같다)
   * undefined  ⇒ ⚠ ★ «구버전 서버»에서만 — 지금 서버는 ⭐ 늘 보낸다
   * ```
   * ⚠ `?:` 로 둔 까닭: 이 꼴은 ★ «응답»을 적는 것이라 ⇒ 옛 서버도 담을 수 있어야 한다.
   *   ⇒ ⛔ 필수로 바꾸면 ★ 이 타입을 손으로 만드는 시험 둘이 «거짓»이 되는데
   *     ⇒ ⚠ 이 레포의 `tsc` 는 ★ 시험 파일을 안 보므로 ⭐ 그 거짓을 «못 잡는다».
   * ⇒ ⇒ ⭐ 그래서 ★ 「서버가 늘 보낸다」는 ⭐ «가드»가 잠근다 —
   *   `pages/ProjectV2/space-v2.guard.test.tsx` 의 ★ 「서버 serialize 의 칸이 DTO 에 다 있다」.
   *   ⇒ ★ 서버가 그 칸을 빼면 ⇒ ⭐ 화면이 거짓말하기 «전»에 그 가드가 빨개진다.
   */
  myRole?: ProjectRole | null;
}

/** 프로젝트 멤버 역할 — prisma `enum ProjectRole` 과 ★ 같은 넷. */
export type ProjectRole = 'OWNER' | 'MANAGER' | 'MEMBER' | 'VIEWER';

export interface ProjectMemberPreviewDto {
  userId: string;
  name: string;
  avatarUrl: string | null;
}

export interface TaskDto {
  id: string;
  projectId: string;
  createdBy: string;
  title: string;
  description: string | null;
  status: string;     // backend 정합 — TaskStatus 4값.
  priority: string;   // TaskPriority 3값.
  dueDate: string | null;
  sortOrder: number;
  assignees: string[];
  tags: string[];
  createdAt: string;
  /*
   * ⭐⭐ 2차 4-4 (2026-10-09) — ★ 서버 `serializeTask` 가 돌려주는 칸을 ⭐ «다» 받는다.
   *
   * ```
   * 쟀다 ⇒ apps/chat-server/src/modules/tasks/tasks-helpers.ts 의 serializeTask
   *   ★ 돌려주는 칸 ⭐ 열아홉 — ⇒ 위 열둘 ＋ 아래 ⭐ 일곱
   * 그리고 ★ 단건(`GET /api/v1/tasks/:id` · tasks.routes.ts:537)이 ⇒ ⭐ 그 일곱을 «다» select 한다
   *   startDate · progress · approvedBy · approvedAt · rejectReason ·
   *   project:{ requiresDoneApproval } ⇒ serializeTask 가 `=== true` 로 펴 준다 ·
   *   canApproveDone ⇒ ★ 라우트가 «계산해 실어 보낸다»
   *     (`effective !== null && canApproveTaskDone(effective)` ⇒ ⛔ 「모른다」를 true 로 안 읽는다)
   * ```
   * ⚠ 전부 `?:` 다 — ⇒ ★ 목록(`/tasks/me`)·생성·수정 응답은 ⭐ 그 칸을 안 준다.
   *   ⇒ ⇒ ⭐ 그래서 ★ 기존 호출자가 ⭐ 하나도 안 깨진다(지운 줄 0 · 더한 줄만).
   * ⚠⚠ 그리고 ★ 이 판(4-4)이 ⭐ «읽는» 것은 ★ 다섯이다 —
   *   requiresDoneApproval · approvedBy · approvedAt · rejectReason · canApproveDone
   *   ⇒ ⛔ `startDate`·`progress` 는 ★ 이 판이 ⭐ 안 읽는다. ⇒ ⇒ ★ 그래도 담는 까닭:
   *     ⇒ ⭐ 이 꼴은 ★ 「서버 응답의 ⭐ «거울»」이다. ⇒ ★ 반쪽 거울은 ⇒ ⛔ 서버에 대한 «거짓말»이 되고
   *       ⇒ ⇒ 다음 사람이 ★ 「서버가 안 준다」로 읽는다. ⇒ ★ 그 둘은 ⭐ 4-5(보드)가 읽는다.
   *   ⇒ ⇒ ⭐ 그 「열아홉 ＝ 열아홉」을 ★ 가드가 ⭐ 서버 소스와 맞대어 잠근다
   *     (`pages/ProjectV2/task-detail.guard.test.ts`).
   */
  startDate?: string | null;
  progress?: number;
  requiresDoneApproval?: boolean;
  approvedBy?: string | null;
  approvedAt?: string | null;
  rejectReason?: string | null;
  canApproveDone?: boolean;
}

export interface CreateProjectInput {
  name: string;
  description?: string;
  dueDate?: string;
  // project-menu-detailing — 팀 소속 생성 (optional, 생략 시 무소속).
  teamId?: string;
  // v2.9 — 제목 링크 (새 창)
  linkUrl?: string;
  /**
   * ★★ 2026-10-04 — **보내는** 쪽 두 칸 (조각 ⓒ · `ProjectCreateSimpleModal` 이 고른다).
   *
   * ⛔ **7자(`#RRGGBB`)만 보낸다.** 서버가 `^#[0-9a-fA-F]{6}$` 로 못 박았다
   *   (`projects.routes.ts:82` · 사용자 결정 2026-10-04 「정본」) ⇒ 8자(`#RRGGBBAA`)는 **400** 이다.
   *   ⚠ 표는 `VarChar(9)` 이고 그 칼럼 COMMENT 는 `'#RRGGBBAA'` 라 **어긋나 보이지만**,
   *     팔레트 12색이 전부 7자이고(실측) 서버도 7자로 잠갔으므로 ⇒ ★ 그 어긋남은 **주석 하나**다.
   *
   * ⚠ **여기서는 좁힌다** — `'TEAM' | 'PRIVATE'`. 위 `TaskProjectDto` 의 `visibility` 는 `string`
   *   으로 **넓게** 두었다. ⇒ ★ 까닭이 반대다: **보내는** 값은 서버 enum 밖이면 **400** 이므로
   *   타입이 미리 막아 주는 것이 맞고, **받는** 값은 좁히면 타입이 거짓말을 한다.
   *
   * ⛔ 이 둘을 **보내도 종전에는 조용히 버려졌다** — Fastify ajv `removeAdditional` 이라 400 도
   *   없이 지워졌다(이 레포가 세 곳에 적어 둔 사실). ⇒ PR #2161 이 ㉮㉯㉰ 를 세워 **이제 들어간다.**
   */
  color?: string;
  visibility?: 'TEAM' | 'PRIVATE';
}

export interface PatchProjectInput {
  name?: string;
  description?: string;
  dueDate?: string;
  isArchived?: boolean;
  /** v2.9 — 제목 링크. null 로 해제. */
  linkUrl?: string | null;
  /** 프로젝트 위임 — 소속 팀 이전. null 이면 팀 해제(개인 프로젝트). 대상 팀의 멤버만 가능. */
  teamId?: string | null;
  /**
   * 완료 승인 필요 — 켜면 [완료]가 «검토 요청»이 된다 (2026-10-12 · 정본 이관).
   *
   * ⛔ 서버가 이 칸을 받기 «전»에는 그 열이 영원히 false 였고, 승인 끝점 넷이
   *   전부 409 `APPROVAL_NOT_ENABLED` 로 막혀 «도달 불가»였다 ⇒ 이 칸이 그 기능의 입구다.
   * ⚠ ajv 가 스키마 밖 칸을 «조용히 지우고 200 을 준다» ⇒ 서버 쪽 네 자리가 함께 있어야 돈다
   *   (ajv ＋ 저장 줄 ＋ select 넷 ＋ 응답 펴기) ⇒ 왕복 가드가 그 짝을 잠근다.
   */
  requiresDoneApproval?: boolean;
}

export interface CreateTaskInput {
  title: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: string;
  sortOrder?: number;
  assigneeUserIds?: string[];
  tags?: string[];
}

export interface PatchTaskInput {
  title?: string;
  description?: string;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: string | null;
  sortOrder?: number;
  // 정본 이관 판 6 (2026-10-13) — 목록보기 표가 쓴다. 서버 patchBodySchema 와 짝(tasks.routes.ts).
  startDate?: string | null;
  progress?: number;
  assigneeUserIds?: string[];
}

/**
 * 프로젝트 목록.
 *
 * ## `mine` — 「내가 «멤버»인 것만」 (2026-10-12 사용자 ⇒ 「권고대로」)
 *
 * ```
 * 사용자 지적 ⇒ 「내 프로젝트는 «내가 만든 것이 아닌데도» 나온다」
 * 까닭 ⇒ 서버가 관리자에게는 멤버십과 무관하게 기관 전체를 준다
 *   (project-member-helpers.ts ⇒ isAdminUserRole(userRole) && !mineOnly ⇒ 전부)
 * ⇒ 서버엔 그 문이 «이미» 있다 ⇒ projects.routes.ts:268
 *     const mineOnly = (req.query …)?.mine === 'true';
 *   그 위 :264 주석 ⇒ 「?mine=true (2026-09-11) — 관리자여도 「내가 멤버인 것」만.
 *                      안 주면 종전 그대로」 · AI 도구 project.list_mine 이 늘 켜고 부른다
 *   ⇒ ⇒ 그래서 이 판은 서버 0 줄 · 마이그 0 · 새 끝점 0 이다.
 * ```
 *
 * ⛔ 기본값은 «끈 채»로 둔다 — 안 주면 종전 그대로다.
 *   호출자가 11 곳인데(대시보드 위젯 · 업무 화면 · 모달 등) 그 화면들은 「내 것」이 아니다.
 *   ⇒ 전부에 켜면 그 화면들이 조용히 좁아진다 ⇒ 가드가 그것을 잠근다.
 *
 * ⚠ 값은 글자로 `'?mine=true'` 다 — 서버가 `=== 'true'` 로만 본다.
 *   `?mine=1` 처럼 쓰면 400 도 안 나고 «조용히 안 걸린다».
 */
export async function listProjects(opts?: { mine?: boolean }): Promise<TaskProjectDto[]> {
  // ⚠ 값은 글자로 '?mine=true' — 서버가 === 'true' 로만 본다(projects.routes.ts:268).
  const res = await apiClient.get<{ projects: TaskProjectDto[] }>('/projects' + (opts?.mine ? '?mine=true' : ''));
  const rows = Array.isArray(res.data?.projects) ? res.data.projects : [];
  // 2026-08-07 — memberPreview.avatarUrl 은 DB raw(`wcavatar:{ts}` 센티널 | 외부 URL)로 오므로
  // 표시용 URL 로 변환한다(project-teams 클라이언트의 resolveMemberAvatar 와 같은 처리).
  // 필드가 없는 구버전 응답은 그대로 통과 — 기존 동작 불변.
  return rows.map((p) => (
    Array.isArray(p.memberPreview)
      ? {
          ...p,
          memberPreview: p.memberPreview.map((m) => ({
            ...m,
            avatarUrl: avatarImageUrl(m.userId, m.avatarUrl),
          })),
        }
      : p
  ));
}

export async function createProject(input: CreateProjectInput): Promise<TaskProjectDto> {
  const res = await apiClient.post<{ project: TaskProjectDto }>('/projects', input);
  return res.data.project;
}

export async function getProject(id: string): Promise<TaskProjectDto> {
  const res = await apiClient.get<{ project: TaskProjectDto }>(`/projects/${id}`);
  return res.data.project;
}

export async function patchProject(id: string, input: PatchProjectInput): Promise<TaskProjectDto> {
  const res = await apiClient.patch<{ project: TaskProjectDto }>(`/projects/${id}`, input);
  return res.data.project;
}

export async function deleteProject(id: string): Promise<void> {
  await apiClient.delete(`/projects/${id}`);
}

export async function listTasks(projectId: string, status?: TaskStatus): Promise<TaskDto[]> {
  const params = status ? { status } : {};
  const res = await apiClient.get<{ tasks: TaskDto[] }>(`/projects/${projectId}/tasks`, { params });
  // 방어: 비정형 응답(HTML SPA fallback·필드 누락 등)이 와도 항상 배열 반환 — 소비처 for..of 크래시 차단.
  return Array.isArray(res.data?.tasks) ? res.data.tasks : [];
}

export async function createTask(projectId: string, input: CreateTaskInput): Promise<TaskDto> {
  const res = await apiClient.post<{ task: TaskDto }>(`/projects/${projectId}/tasks`, input);
  return res.data.task;
}

export async function patchTask(taskId: string, input: PatchTaskInput): Promise<TaskDto> {
  const res = await apiClient.patch<{ task: TaskDto }>(`/tasks/${taskId}`, input);
  return res.data.task;
}

export async function deleteTask(taskId: string): Promise<void> {
  await apiClient.delete(`/tasks/${taskId}`);
}

/** 시안 line 1883~1888 매핑. backend SCREAMING_SNAKE 와 정합.
 *  fallback hex 제거 (tokens.css 의 --accent-* 6종 + --text-muted 정의 완료).
 *  emoji 필드는 deprecated (2026-06-10) — UI 는 TaskStatusIcon(인라인 SVG) 사용, 신규 렌더에 emoji 사용 금지. */
export const TASK_STATUS_META: Record<TaskStatus, { label: string; emoji: string; color: string }> = {
  TODO:        { label: '할 일',   emoji: '📋', color: 'var(--text-muted)' },
  IN_PROGRESS: { label: '진행 중', emoji: '🚀', color: 'var(--accent-blue)' },
  REVIEW:      { label: '리뷰',    emoji: '👀', color: 'var(--accent-orange)' },
  DONE:        { label: '완료',    emoji: '✅', color: 'var(--accent-green)' },
};

export const TASK_STATUSES: TaskStatus[] = ['TODO', 'IN_PROGRESS', 'REVIEW', 'DONE'];

export const TASK_PRIORITY_META: Record<TaskPriority, { label: string; color: string }> = {
  HIGH:   { label: '높음', color: 'var(--accent-red)' },
  MEDIUM: { label: '보통', color: 'var(--accent-orange)' },
  LOW:    { label: '낮음', color: 'var(--accent-green)' },
};

export const TASK_PRIORITIES: TaskPriority[] = ['HIGH', 'MEDIUM', 'LOW'];

// ─── project-menu-detailing (2026-06-10) — 프로젝트 횡단 내 할일 목록 (additive) ───
// backend GET /api/v1/tasks/me — interface-project-menu.md §3.

export interface MyTaskDto {
  id: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  tags: string[];
  project: {
    id: string;
    name: string;
    teamId: string | null;
    teamName: string | null;
    /** 완료 승인 (2026-09-04) — 행마다 체크의 뜻이 갈리므로 프로젝트 플래그를 행과 함께 받는다. */
    requiresDoneApproval?: boolean;
  };
  /*
   * ⭐⭐ 2026-10-09 (4-5) — ★ 완료 승인 다섯 칸. ⇒ 정본(`feature/dev-syk`) 선언을 «글자 그대로».
   *   ⇒ 4-4 에서 `TaskDto`(상세)에만 더한 것의 ★ «짝»이다 — 목록 쪽이 이쪽이다.
   *
   * ## ⛔⛔ 그런데 ★ 서버 `/tasks/me` 는 ⇒ ⭐ 이 다섯을 «안 준다» (2026-10-09 실측)
   * ```
   * 잰 명령  git show origin/main:apps/chat-server/src/modules/tasks/tasks.routes.ts
   * ⇒ `/api/v1/tasks/me` 핸들러 ⇒ :1641 에서 열리고 :1717 에서 닫힌다 (그 파일의 ★ 마지막 라우트)
   * ⇒ ★ 그 안에서 ⇒ ⭐ prisma `select` 는 :1675–:1690 · 응답 `map` 은 :1695–:1713
   * ⇒ ⇒ ⭐ 응답 `map` 이 내놓는 칸 ＝ ★ ⭐ 열하나:
   *     id · title · status · priority · dueDate · description · createdAt · createdBy
   *     · assignees · tags · project{id,name,teamId,teamName}
   *   ⇒ ⚠ 그 밖에 ★ `total` 이 ⭐ «map 바깥»에 하나 더 있다(⇒ 줄의 칸이 아니라 ★ 묶음의 수다)
   *     ⇒ ⇒ ⛔ 2026-10-09 에 ★ 처음 적을 때 ⭐ 그 둘을 섞어 ★ 「열둘」이라 적었다 ⇒ ⇒ ⭐ 정정한다.
   *       ⇒ ★ 4-5 가드 ③ⓐ 가 ⇒ ⭐ 그 수를 ★ `toBe(11)` 로 ⭐ 못 박는다.
   *   ⇒ ⚠⚠ 그리고 ★ 그 열하나 중 ⭐ «넷»은 ⇒ ★ 이 DTO 가 ⭐ «선언하지 않는다» —
   *     ⇒ description · createdAt · createdBy · assignees
   *     ⇒ ⇒ ★ 서버 주석이 까닭을 적는다 — 「모바일 상세 파리티(additive)」 ⇒ ⭐ «앱»이 쓰는 칸이다
   *     ⇒ ⇒ ⭐ 남는 칸은 ★ 런타임에 해롭지 않다(그냥 안 읽힌다) ⇒ ⇒ 가드 ③ⓑ 가 ★ 그 넷을 센다
   * ⇒ ⇒ ⛔ 그 열하나에 ★ `requiresDoneApproval` · `canApproveDone` · `rejectReason`
   *     · `approvedBy` · `approvedAt` ⇒ ⇒ ⭐ «하나도 없다» · `progress` 도 없다
   * ⇒ ⭐ [대조군] 그 이름들이 ★ 그 파일에 «있기는» 하다 — ⇒ 다만 ⭐ 자리가 다르다:
   *     :596–:619 ⇒ 그것은 `GET /tasks/:id`(상세)의 select·serialize 다 ⇒ ★ :1641 «앞»이다
   *     ⇒ ⇒ ★ 즉 「파일에 그 이름이 있다」로 재면 ⭐ 거짓 확증이 된다 — ★ 줄 자리로 갈랐다
   * ⇒ ⭐ [대조군] 없는 이름 `requiresNoSuchThing` ⇒ 0건 (⇒ 훑기가 죽은 것이 아니다)
   * ```
   * ⇒ ⇒ ⭐⭐ 그 뜻 ⇒ ★ 보드의 승인 UI 네 갈래가 ⇒ ⭐ «지금은 도달 불가»다:
   *   `MyTasksBoard.tsx` :419 · :506 · :514 · :541 ⇒ 전부 `t.project.requiresDoneApproval === true`
   *   ⇒ ⇒ ★ 그 값이 늘 `undefined` 라 ⇒ ⭐ 「완료 요청」 흐름이 ★ 안 열리고 ⇒ 종전처럼 바로 완료된다
   *   ⇒ ⇒ ⇒ ⚠ 그것은 ★ «깨짐»이 아니라 ⭐ 「승인 기능이 꺼진 프로젝트」와 ★ 같은 모습이다
   *     ⇒ ⇒ ⛔ 그래서 ★ 눈으로는 ⭐ 안 보인다 — ★ 그래서 ⭐ 여기 적는다.
   * ⇒ ⭐ 할 일 ⇒ ★ 백엔드에 «한 자리 · 전부 더하기» ⇒ 관제에 올렸다(2026-10-09):
   *     ① `select` 에 approvedBy·approvedAt·rejectReason ＋ project.requiresDoneApproval
   *     ② `map` 에 그 넷 ＋ 계산된 `canApproveDone`
   *        (⇒ ★ 판정 함수는 ⭐ 이미 있다 — 같은 파일 :618 이 `canApproveTaskDone(effective)` 를 쓴다)
   * ⇒ ⚠⚠ **만료 조건** ⇒ ★ `/tasks/me` 응답 `map` 이 ⭐ `requiresDoneApproval` 을 내놓기 시작하면
   *   ⇒ ⇒ ★ 이 글을 ⭐ 뒤집고(지우지 않고) ⇒ ⇒ ★ 4-5 가드의 ⭐ 「도달 불가」 기록도 함께 뒤집는다.
   *   ⇒ ⚠ 그때 ★ 가드가 울릴 것이다 — ⭐ 그것이 «의도»다. ⛔ 울린 가드를 지우지 말고 ★ 뒤집어라.
   *
   * ## ✅✅✅ 2026-10-09 (같은 날 · 몇 시간 뒤) — ★ 그 만료 조건이 ⭐ «울렸다». ⇒ ★ 뒤집는다
   *
   * ⛔ **위 글의 「서버 `/tasks/me` 는 이 다섯을 «안 준다»」는 ★ 이제 ⭐ «거짓»이다.**
   *   ⇒ ⭐ 잰 때는 참이었고 ⇒ ★ 백엔드가 세웠다. ⇒ ⇒ ★ 지우지 않고 ⭐ 뒤집어 둔다 —
   *     ⇒ ⇒ ★ 「⭐ 왜 한때 도달 불가였나」와 ⇒ ★ 「⭐ 그것을 어떻게 쟀나」가 ★ 다음 사람에게 쓸모가 있다.
   * ```
   * 커밋  f9fe9ca36 feat(tasks): /tasks/me 가 완료 승인 다섯을 내놓는다
   *                 — ⭐ 4-5 의 승인 UI 가 도달 불가였다        (⇒ #2358)
   * ⇒ ★ 응답 `map` 이 내놓는 칸 ＝ ⭐ 열하나 → ★ ⭐ 열다섯
   *     ＋ approvedBy · approvedAt · rejectReason · canApproveDone   ⇐ ★ 최상위 넷
   * ⇒ ★ 그리고 ⭐ `requiresDoneApproval` 은 ⇒ ★ ⭐ «`project` 안»에 들어왔다
   *     ⇒ 그 줄 주석: 「`project` «안»이다(바깥이 아니다) — 4-5 가 `t.project.…` 로 읽는다」
   *     ⇒ ⇒ ✅ 즉 ★ 내가 적은 ⭐ 「`MyTaskDto.project` «안»이다」가 ★ 그대로 반영됐다
   * ⇒ ⚠ `canApproveDone` 은 ★ 서버가 ⭐ `canApproveFor(projectId)` 로 «계산»해 싣는다
   *     ⇒ ⇒ ★ 즉 ⭐ 클라이언트가 역할을 ★ 추측하지 ⛔ 않는다(⇒ 정본 주석이 바란 그 꼴이다)
   * ⇒ ⇒ ⭐⭐ 그러므로 ★ 보드의 승인 UI ⭐ 네 갈래(:419 · :506 · :514 · :541)가
   *      ⇒ ⇒ ★ 이제 ⭐ «도달 가능»하다.
   * ```
   * ⇒ ⚠ 그래도 ★ 이 다섯은 ⭐ `?:` 로 ★ 둔다 — ⇒ ⭐ 「받는 쪽은 넓게」(이 파일의 다른 칸과 같은 까닭).
   *   ⇒ ⇒ ★ 서버가 한 판 뒤로 돌아가도 ⭐ 타입이 거짓말하지 않게 하고,
   *     ⇒ ⇒ ★ 「⭐ 실제로 오나」는 ⇒ ⭐ 가드가 «서버 소스를 읽어» 잠근다
   *       (`pages/ProjectV2/task-board.guard.test.ts` ③ⓒ ⇒ ★ 최상위 넷 ＋ project 안 하나).
   * ⇒ ⚠⚠ **남은 만료 조건 ⇒ `progress`.** ⇒ ★ 서버 `schema.prisma` 의 `model Task` 에 그 칼럼이
   *   ⭐ 0건이라 ⇒ ★ 여전히 ⭐ 늘 `undefined` 다. ⇒ ⇒ 가드 ③ⓑ 가 ★ 그것을 들고 있다.
   */
  approvedBy?: string | null;
  approvedAt?: string | null;
  rejectReason?: string | null;
  /** 이 행을 내가 승인할 수 있는가 — 프로젝트마다 내 역할이 달라 행 단위로 온다. */
  canApproveDone?: boolean;
  /**
   * 진행률 0~100 — 정본(`feature/dev-syk`)의 칸을 «글자 그대로» 옮겼다 (2026-10-01).
   * 정본 주석: 「목록보기에서 사용자가 직접 조정하는 값이라 서버가 계산하지 않는다.
   *   옛 서버 응답에는 없어 optional — 없으면 막대를 그리지 않는다」
   *
   * ⛔ **그런데 지금 서버는 이 칸을 «아예 안 준다».** 재서 확인한 것(2026-10-01):
   *   `prisma/schema.prisma` 의 `model Task` 에 `progress` 칼럼 «0건» (대조군: 같은 모델의
   *   `priority` 는 1건 — 훑기가 죽은 것이 아니다).
   * ⇒ ★ 그래서 이 칸은 «늘 undefined» 이고, 읽는 쪽은 전부 `?? 0` 으로 받는다
   *   (`MyTasksRowExtras.summarizeGroup`). 「평균 진척률」은 **지금은 늘 0%** 다.
   * ⇒ ⛔ 그 칸이 화면에 붙는 판은 «그 전에» 서버 칸을 세워야 한다 — 안 그러면 0% 를 보여 준다.
   *   그 사정을 ProjectPage 대장의 「어느 화면이 어느 서버 조각을 기다리나」 칸에 적었다.
   *
   * ## ⛔⛔ 2026-10-09 (4-5 뒤) — ★ 위 「칼럼 ⭐ 0건」은 ⇒ ★ ⭐ «이제 거짓»이다. ⇒ 정정한다
   *
   * ⇒ ★ 위 글은 ⭐ 2026-10-01 측정이고 ⇒ ★ 그때는 ⭐ 참이었다. ⇒ ⇒ ★ 그 사이 ⭐ 칼럼이 생겼다:
   * ```
   * 잰 명령  awk '/^model Task \{/,/^\}/' prisma/schema.prisma   ⇒ ★ 그 블록 «안»에서
   *   progress      Int          @default(0)
   *   // progress : 0~100. 서버가 값을 계산하지 않는다 — 사용자가 목록에서 직접 조정한다.
   * [대조군] 같은 블록 priority ⇒ 1  ·  없는 이름 NOSUCHCOL ⇒ 0  (⇒ 훑기가 죽은 것이 아니다)
   * ⇒ ★ 들어온 때 ⇒ git log -S progress -- prisma/schema.prisma
   *     c91ca83ab  ⭐ 2026-10-06  「마이그 219 ＋ 선언 — 업무 보드 확장(조각 2-c)」
   * ```
   * ⇒ ⚠⚠ ★ 그러니 ⭐ 「서버 칸을 세워야 한다」는 ⇒ ★ ⭐ 이미 끝난 일이다. ⇒ ⛔ 위 글로 판단하지 말 것.
   *
   * ## ⭐ 그런데 ★ 「늘 0」은 ⇒ ⭐ «여전히» 참이다 — ★ 까닭이 ⭐ 바뀌었다
   * ```
   * ⓐ ⛔ `/tasks/me` 가 ★ 그 칸을 ⭐ «안 내놓는다»
   *    ⇒ ★ 그 응답 map 의 칸 열다섯에 ⭐ `progress` 가 ★ 없다(4-5 가드 ③ⓐ·③ⓑ 가 그것을 잠근다)
   * ⓑ ⛔ 그리고 ★ ⭐ 「⭐ 그 칸을 ★ «쓰는» 끝점」이 ⇒ ★ ⭐ 0건이다 — 2026-10-09 실측:
   *    프론트  `PatchTaskInput` 에 progress ⇒ ⭐ 0
   *    서버    grep -rn progress apps/chat-server/src | grep -E "body|properties|data:" ⇒ ⭐ 0건
   *    [대조군] 같은 꼴로 `priority` ⇒ ★ 둘이 나온다 —
   *      tasks.routes.ts:483  priority: (body.priority ?? 'MEDIUM') as any,            ⇐ 생성
   *      tasks.routes.ts:682  if (body.priority !== undefined) updateData.priority = …  ⇐ 수정
   *      ⇒ ⇒ ✅ 그러므로 ★ 그 거르개는 ⭐ «살아 있다».
   * ⇒ ⇒ ⭐⭐ 그 뜻 ⇒ ★ 아무도 ⭐ 그 칸을 ★ 못 쓴다 ⇒ ⇒ 모든 줄의 값이 ⭐ `@default(0)` ＝ 0
   *   ⇒ ⇒ ⇒ ✅ 그래서 ★ 화면의 「0%」는 ⇒ ⭐ «DB 값과 같다» — ⛔ 「거짓 정보」가 ★ 아니다.
   *     ⇒ ⚠ 나는 2026-10-09 에 ★ 그것을 ⭐ 「틀린 정보」라 적어 ⇒ ★ 관제에 ⭐ 켜기를 미루자고 했다.
   *       ⇒ ⇒ ⛔ 그 판단은 ★ 위 「칼럼 0건」이라는 ⭐ «낡은 전제»에서 나왔다 ⇒ ⇒ ★ 철회했다.
   * ```
   *
   * ## ⚠⚠ 남은 위험 ⇒ ★ 「⭐ 쓰는 끝점이 먼저 생기는」 순서
   * ```
   * ⇒ ★ 누가 ⭐ `progress` 를 «쓰는» 끝점을 먼저 세우면 ⇒ ⇒ ⛔ 보드는 ★ 여전히 0% 를 보여 준다
   *   ⇒ ⇒ ★ 까닭 ⇒ ⭐ `/tasks/me` 가 ★ 그 칸을 ⭐ 안 내놓으므로.
   *   ⇒ ⇒ ⇒ ⭐ «그때»가 ★ 「0% 가 ⭐ 거짓말이 되는」 ★ 그 순간이다.
   * ⇒ ⚠ 4-5 가드 ③ⓑ 는 ★ 「⭐ 서버가 ★ 내놓기 시작하면」 울린다 ⇒ ⇒ ⛔ «쓰는 쪽»은 ★ 안 본다
   *   ⇒ ⇒ ✅ 그래서 ★ 그 가드에 ⭐ 트리거를 ★ 하나 더 달았다 — ⇒ 「받는 본문·스키마가 생기면」.
   *     ⇒ ⇒ ★ 둘 중 ⭐ 어느 쪽이 먼저 와도 ⇒ ★ ⭐ 울린다.
   * ```
   * ✅ 2026-10-13 (정본 이관 판 6) — ★ 그 순서를 피했다: 같은 판에서 «쓰는 쪽»(PATCH 스키마 ·
   *   body · updateData)과 «내놓는 쪽»(/tasks/me select ＋ map)을 ⭐ 함께 세웠다. 가드 ③ 은 이제 둘 다 «있다»를 잰다.
   */
  progress?: number;
}

export async function listMyTasks(params?: {
  status?: TaskStatus[];
  /**
   * ⭐ 마감 필터 (2026-10-09 · 4-5). ⇒ 서버가 선 뒤에 넣었다 — 까닭은 `MyTasksDueFilter` 머리말.
   * ⇒ ⚠ 값은 서버 enum 과 ★ 같아야 한다 — 다르면 ajv 가 ⭐ 400 이 아니라 «조용히 지운다».
   */
  due?: MyTasksDueFilter;
  limit?: number;
  offset?: number;
}): Promise<{ items: MyTaskDto[]; total: number }> {
  const res = await apiClient.get<{ items: MyTaskDto[]; total: number }>('/tasks/me', {
    params: {
      ...(params?.status?.length ? { status: params.status.join(',') } : {}),
      ...(params?.due ? { due: params.due } : {}),
      ...(params?.limit !== undefined ? { limit: params.limit } : {}),
      ...(params?.offset !== undefined ? { offset: params.offset } : {}),
    },
  });
  // 방어 정규화(additive) — 2xx 인데 본문이 비-JSON/형태불일치(예: SPA fallback HTML)여도 안전한 형태 보장.
  const data = res.data as { items?: MyTaskDto[]; total?: number } | undefined;
  return { items: Array.isArray(data?.items) ? data!.items : [], total: data?.total ?? 0 };
}

// ============================================================
// v2.8 (2026-06-11) — 태스크 협업: 코멘트 + 파일 첨부
// 업로드 자체는 api/files.ts(presigned) 재사용 — 여기는 task 연결 CRUD.
// ============================================================

export interface TaskCommentDto {
  id: string;
  content: string;
  createdAt: string;
  authorId: string;
  authorName: string;
  /** 표시용 URL (wcavatar 센티널 해석 완료). */
  authorAvatarUrl: string | null;
}

export interface TaskAttachmentDto {
  id: string;
  fileId: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number;
  verified: boolean;
  addedBy: string;
  addedByName: string;
  createdAt: string;
}

function resolveCommentAvatar(c: TaskCommentDto): TaskCommentDto {
  return { ...c, authorAvatarUrl: avatarImageUrl(c.authorId, c.authorAvatarUrl) };
}

export async function listTaskComments(taskId: string): Promise<TaskCommentDto[]> {
  const res = await apiClient.get<{ comments: TaskCommentDto[] }>(`/tasks/${taskId}/comments`);
  return (Array.isArray(res.data?.comments) ? res.data.comments : []).map(resolveCommentAvatar);
}

export async function createTaskComment(taskId: string, content: string): Promise<TaskCommentDto> {
  const res = await apiClient.post<{ comment: TaskCommentDto }>(`/tasks/${taskId}/comments`, { content });
  return resolveCommentAvatar(res.data.comment);
}

export async function deleteTaskComment(taskId: string, commentId: string): Promise<void> {
  await apiClient.delete(`/tasks/${taskId}/comments/${commentId}`);
}

export async function listTaskAttachments(taskId: string): Promise<TaskAttachmentDto[]> {
  const res = await apiClient.get<{ attachments: TaskAttachmentDto[] }>(`/tasks/${taskId}/attachments`);
  return Array.isArray(res.data?.attachments) ? res.data.attachments : [];
}

export async function addTaskAttachment(taskId: string, fileId: string): Promise<{ id: string }> {
  const res = await apiClient.post<{ id: string }>(`/tasks/${taskId}/attachments`, { fileId });
  return res.data;
}

export async function deleteTaskAttachment(taskId: string, attachmentId: string): Promise<void> {
  await apiClient.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
}

// ─── 2차 4-4 업무 상세 패널 (2026-10-09) — ⭐ 정본 선언을 «글자 그대로» ───────────

/**
 * ⭐ 아래 다섯은 ★ 정본(`feature/dev-syk`)의 `api/tasks.ts` 선언을 ⭐ «글자 그대로» 옮겼다.
 * ⇒ ★ 이 판의 `MyTaskDetailPanel` 이 ⭐ 다섯을 «다» 부른다 — ⇒ ⛔ 부르는 곳 없는 것은 안 담았다.
 *
 * ```
 * 서버가 있나 ⇒ ⭐ 쟀다(2026-10-09 · main 8def559d0 의 서버 소스):
 *   GET  /api/v1/tasks/:id          ⇐ tasks.routes.ts:537   ⇒ getTask
 *   POST /api/v1/tasks/:id/submit   ⇐ task-approval.routes.ts ⇒ submitTaskForApproval
 *   POST /api/v1/tasks/:id/approve  ⇐ 같은 파일               ⇒ approveTask
 *   POST /api/v1/tasks/:id/reject   ⇐ 같은 파일               ⇒ rejectTask
 * ⚠ 그 단건 라우트는 ★ 어제(2026-10-08)까지 ⭐ «없었다» — ⇒ PATCH·DELETE 만 있었다.
 *   ⇒ ⇒ ★ 그래서 ⭐ 이 판을 «멈추고» 서버를 먼저 세웠다(백엔드 #2316). ⇒ 그 경위를 적어 둔다.
 * ```
 * ⚠ `TaskApprovalResultDto` 는 ★ 승인 셋이 ⭐ 공통으로 돌려주는 꼴이다 —
 *   ⇒ ⛔ `TaskDto` 가 ★ 아니다(상태 갱신에 필요한 넷만 온다).
 */
/** 승인 라우트 셋이 공통으로 돌려주는 모양 — 한 자리에서 상태를 갱신할 수 있게 같은 것을 준다. */
export interface TaskApprovalResultDto {
  id: string;
  status: TaskStatus;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectReason: string | null;
}

/** AW-004-03 (2026-08-18) — 업무 단건. 목록 요약에 없는 설명·담당자·태그까지 준다. */
export async function getTask(taskId: string): Promise<TaskDto> {
  const res = await apiClient.get<{ task: TaskDto }>(`/tasks/${taskId}`);
  return res.data.task;
}

/** 담당자의 [완료 요청] — status 를 REVIEW 로 올린다. 두 번 눌러도 안전(서버가 멱등). */
export async function submitTaskForApproval(taskId: string): Promise<TaskApprovalResultDto> {
  const res = await apiClient.post<{ task: TaskApprovalResultDto }>(`/tasks/${taskId}/submit`);
  return res.data.task;
}

/** 승인자의 [승인] — DONE + 승인자·승인시각 기록. */
export async function approveTask(taskId: string): Promise<TaskApprovalResultDto> {
  const res = await apiClient.post<{ task: TaskApprovalResultDto }>(`/tasks/${taskId}/approve`);
  return res.data.task;
}

/** 승인자의 [반려] — IN_PROGRESS 로 되돌리고 사유를 남긴다. 사유는 비워도 된다. */
export async function rejectTask(taskId: string, reason?: string): Promise<TaskApprovalResultDto> {
  const res = await apiClient.post<{ task: TaskApprovalResultDto }>(`/tasks/${taskId}/reject`, {
    ...(reason && reason.trim() ? { reason: reason.trim() } : {}),
  });
  return res.data.task;
}

// ─── 2차 4-5 보드 ＋ 배선 (2026-10-09) — ⭐ 정본 선언을 «글자 그대로» ───────────

/**
 * ⭐ 아래 셋은 ★ 정본(`feature/dev-syk`)의 선언을 ⭐ «글자 그대로» 옮겼다.
 * ⇒ ★ 이 판의 `MyTasksBoard` 가 ⭐ 셋을 «다» 쓴다 — ⇒ ⛔ 부르는 곳 없는 것은 안 담았다.
 *
 * ```
 * 서버가 있나 ⇒ ⭐ 쟀다(2026-10-09 · main 4adecb6ed 의 서버 소스):
 *   GET /api/v1/tasks/approvals/pending   ⇐ task-approval.routes.ts   ⇒ listPendingApprovals  ✅
 * ⇒ ⚠ 그 끝점은 ★ Task 표만 쓴다 ⇒ ⇒ ⭐ 마이그 219 와 ★ 무관하다(⇒ 219 는 체크리스트·묶음·칸의 표다).
 * ```
 *
 * ## ⛔⛔ `MyTasksDueFilter` — ★ 타입은 담지만 ⇒ ⭐ `listMyTasks` 의 칸은 ★ «아직» 안 넣는다
 *
 * ```
 * 정본 보드 :159  listMyTasks({ limit, offset, ...(dueFilter ? { due: dueFilter } : {}) })
 * ⇒ ⛔ 그런데 ★ main 서버의 `/api/v1/tasks/me` 질의 스키마는 ⇒ ⭐ { status, limit, offset } 이고
 *   ⇒ ⇒ `additionalProperties: false` 다 ⇒ ⇒ ★ ajv 가 ⭐ 400 이 아니라 «조용히 지운다».
 *   ⇒ ⇒ ⇒ ⛔ 그래서 ★ 칸을 지금 넣으면 ⇒ ⭐ 마감 필터 칩이 «눌리는데 아무 일도 안 한다».
 *     ⇒ ⇒ ★ 그것은 ⭐ 「라벨만 남고 일은 안 하는 판」이고 ⇒ ⇒ ★ 사보타주로도 ⭐ 안 갈린다.
 * ⇒ ✅ 정본 서버에는 ★ 있다(tasks.routes.ts:1648 `due: { enum: ['soon','overdue','risk'] }`)
 *   ⇒ ⭐ 그리고 ★ main 에 ⭐ SSOT 헬퍼가 «이미» 있다 — `task-due-window.ts` 의
 *     taskDueWindow · overdueDueFilter · dueSoonDueFilter (⇒ export 목록이 ★ 정본과 같다)
 *   ⇒ ⇒ ★ 다만 `tasks.routes.ts` 가 ⭐ 그 파일을 «안 부른다»(import 0건)
 * ⇒ ⇒ ⇒ ⭐ 그러므로 ★ 백엔드 일은 ⭐ «한 파일 · 전부 더하기»다 ⇒ 관제에 올렸다(2026-10-09).
 *   ⇒ ⚠ 서버가 서면 ★ 이 자리에 ⭐ `due?: MyTasksDueFilter` 를 더하고 ⇒ 보드를 붙인다.
 * ```
 *
 * ## ✅✅ 2026-10-09 (같은 날 · 몇 시간 뒤) — ★ 그 만료 조건이 ⭐ «울렸다» ⇒ ⇒ ★ 칸을 넣었다
 *
 * ⛔ **위 글의 「main 서버의 질의 스키마는 `{ status, limit, offset }`」은 ★ 이제 «거짓»이다.**
 *   ⇒ ⭐ 잰 때는 참이었고 ⇒ ★ 그 사이 ⭐ 백엔드가 세웠다. ⇒ ⇒ ★ 지우지 않고 ⭐ 뒤집어 둔다 —
 *     ⇒ ⇒ ★ 「왜 한때 보류였나」가 ⭐ 다음 사람에게 쓸모가 있기 때문이다(ajv 의 그 성질).
 * ```
 * 잰 명령  git show origin/main:apps/chat-server/src/modules/tasks/tasks.routes.ts
 * ⇒ ★ `myTasksQuerySchema` 안에 ⭐ 있다:
 *     due: { type: 'string', enum: ['soon', 'overdue', 'risk'] }
 * ⇒ ★ 그리고 ⭐ 핸들러 타입도 ⇒ interface MyTasksQuery { status?; due?: 'soon'|'overdue'|'risk'; … }
 * ⇒ ⇒ ⭐ 그 자리 주석이 ★ 이 보류의 «까닭»을 그대로 인용하고 있다 —
 *     「이 칸이 없으면 ajv 가 400 이 아니라 조용히 지운다 ⇒ 4-5 보드의 칩이 눌리는데 목록이 안 바뀌었다」
 * ⇒ ⇒ ⇒ ✅ 그러므로 ★ 이제 ⭐ 넣어도 «일을 한다». [대조군] 없는 이름 `requiresNoSuchThing` ⇒ 0건
 * ```
 *
 * ## ⛔⛔⛔ ★ 그리고 ⇒ ⭐ 여기서 «더 나쁜 것»이 나왔다 — ★ 이 틈은 ⭐ `tsc` 로 «못 잡는다»
 *
 * ⇒ ★ 칸이 ⭐ 없던 동안에도 ⇒ ⇒ ⛔ `tsc` 가 ★ 한 번도 ⭐ 울리지 않았다. ⇒ ★ 까닭을 쟀다:
 * ```
 * 정본 보드 :159  listMyTasks({ limit, offset, ...(dueFilter ? { due: dueFilter } : {}) })
 *                                               ↑ ★ 「펼치기」다
 * ⇒ ⭐ TypeScript 의 «남는 칸 검사»(excess property check)는 ★ 객체 리터럴에만 돈다
 *   ⇒ ⇒ ⛔ 펼친 칸은 ★ 그 검사를 ⭐ «빠져나간다»
 *
 * ★ 돌려서 쟀다 (2026-10-09 · 탐침 파일을 만들어 돌리고 지웠다):
 *   ⓐ listMyTasks({ limit: 1, ...(a ? { NOSUCHFIELD: a } : {}) })  ⇒ ⛔ 오류 0  (조용하다)
 *   ⓑ listMyTasks({ limit: 1, NOSUCHFIELD: 'soon' })               ⇒ ✅ TS2353  (걸린다)
 *   ⇒ ⇒ ⭐ 즉 ★ 같은 틈인데 ⇒ ⭐ «적는 꼴»이 검사를 켜고 끈다
 * ```
 * ⇒ ⇒ ⭐⭐ 그래서 ★ 이 틈은 ⇒ ⭐ «두 겹»으로 조용했다:
 * ```
 * 앞쪽(프론트) ⇒ 펼치기라 ★ tsc 가 안 본다
 * 뒤쪽(서버)   ⇒ ajv `additionalProperties: false` 가 ★ 400 이 아니라 ⭐ 조용히 지운다
 * ⇒ ⇒ ⛔ 두 관문이 ★ 둘 다 조용하므로 ⇒ ⇒ ⭐ 「칩이 눌리는데 목록이 안 바뀐다」가 ★ 유일한 증상이었다
 * ```
 * ⇒ ⭐ 그리고 ★ 읽기·쓰기가 ⇒ ⭐ «갈린다» — 이것을 외워 둘 것:
 * ```
 * 읽기  t.project.requiresDoneApproval   ⇒ ✅ 칸이 없으면 tsc 가 잡는다 (TS2339 — 실제로 9건 났다)
 * 쓰기  { ...(c ? { due: v } : {}) }      ⇒ ⛔ 칸이 없어도 tsc 가 ★ 안 잡는다
 * ⇒ ⇒ ★ 그러므로 ⭐ 「서버에 없는 칸을 보내는」 결함은 ⇒ ★ 반드시 ⭐ «시험»이 잡아야 한다
 * ```
 */
export type MyTasksDueFilter = 'soon' | 'overdue' | 'risk';

/** 승인 대기 한 건 — 승인자가 보는 목록의 행. */
export interface PendingApprovalDto {
  id: string;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate: string | null;
  progress: number;
  /** 완료 요청이 들어온 시각(서버의 updatedAt). 오래 기다린 것부터 정렬돼 온다. */
  requestedAt: string;
  rejectReason: string | null;
  assignees: Array<{ id: string; name: string }>;
  project: { id: string; name: string; requiresDoneApproval: boolean };
}

/**
 * 승인자가 볼 「완료 승인 대기」 목록.
 *
 * 승인자는 그 업무의 담당자가 아니라서 '내 업무' 목록에 안 뜬다 — 이 목록이 없으면
 * 결재를 기다리는 업무를 찾을 길이 없다.
 */
export async function listPendingApprovals(params?: { limit?: number; offset?: number }): Promise<{
  items: PendingApprovalDto[];
  total: number;
}> {
  const res = await apiClient.get<{ items: PendingApprovalDto[]; total: number }>(
    '/tasks/approvals/pending',
    {
      params: {
        ...(params?.limit !== undefined ? { limit: params.limit } : {}),
        ...(params?.offset !== undefined ? { offset: params.offset } : {}),
      },
    },
  );
  return { items: res.data?.items ?? [], total: res.data?.total ?? 0 };
}

/**
 * ⭐ **휴지통** (정본 2026-09-07 · 우리 이식 2026-10-10) — 프로젝트 DELETE 는 원래부터
 * soft-delete(`deletedAt`) 였는데 꺼내 볼 길이 없어 사실상 영구 삭제였다. 되돌릴 수 있는
 * 것만 보여 주고 되돌린다. 권한은 서버가 DELETE 와 같은 규칙으로 판정한다
 * (지울 수 있었던 사람만 복원 가능).
 *
 * ## ⭐⭐ 이 꼴은 ★ «정본 타입»이 아니라 ⇒ ⭐ «서버 소스»에서 읽었다
 *
 * ⛔ 정본의 `interface` 를 믿고 베끼면, 정본이 서버와 어긋나 있을 때 ★ 그 어긋남을 함께
 *   들여온다. ⇒ ★ 그리고 그 어긋남은 ⭐ **두 관문이 다 침묵한다**:
 *   · 읽기 쪽 — tsc 는 «선언한 꼴»만 보므로 서버가 안 주는 칸을 읽어도 ★ 조용하다
 *   · 쓰기 쪽 — `{...(c ? {x} : {})}` 펼친 칸은 ⛔ 남는-칸 검사를 빠져나간다
 *     ＋ 서버 ajv `additionalProperties: false` 는 400 이 아니라 ★ «조용히 지운다»
 *   ⇒ ⇒ [[spread-into-an-arg-escapes-excess-property-check]]
 *
 * ⇒ ✅ 그래서 **서버 라우트를 열어 응답 꼴을 읽었다** (2026-10-10 실측):
 * ```
 * apps/chat-server/src/modules/tasks/projects.routes.ts:712-754
 *   GET  /api/v1/projects/trash
 *     ⇒ { projects: Array<{ id, name, deletedAt, taskCount, teamName }> }   ⇐ 칸 ★ 다섯
 * apps/chat-server/src/modules/tasks/projects.routes.ts:756-806
 *   POST /api/v1/projects/:id/restore
 *     ⇒ { id, restored: true }                                              ⇐ 우리는 ★ 안 읽는다
 * ```
 *   ⇒ ⭐ 재 보니 정본 선언과 ★ 서버 응답이 **글자까지 같았다** ⇒ ⇒ 그래서 그대로 쓴다.
 *     ⇒ ⚠ 「같다」가 아니라 ★ 「**재서** 같았다」가 요점이다 — 다음 사람이 다시 재야 한다.
 *
 * ⚠ **만료 조건** — 위 두 라우트의 응답 칸이 바뀌면 이 선언이 조용히 낡는다.
 *   ⇒ `task-trash.guard.test.ts` 가 **서버 소스를 직접 읽어** 그 다섯 칸을 맞댄다.
 */
export interface TrashedProjectDto {
  id: string;
  name: string;
  /** ISO 8601 — 지운 시각. */
  deletedAt: string;
  /** 함께 딸려 들어간 살아 있는 태스크 수. */
  taskCount: number;
  teamName: string | null;
}

export async function listTrashedProjects(): Promise<TrashedProjectDto[]> {
  const res = await apiClient.get<{ projects: TrashedProjectDto[] }>('/projects/trash');
  return Array.isArray(res.data?.projects) ? res.data.projects : [];
}

export async function restoreProject(projectId: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/restore`);
}

/**
 * ★ 완전 삭제 — ⇒ ⛔ 되돌릴 수 없다. (2026-10-10 사용자 결정 · 단추 2026-10-12)
 *
 * ⚠ `DELETE` 가 아니라 `POST` 다 — ⇒ 본문(`confirmName`)을 받아야 하기 때문이다.
 *   까닭은 서버가 들고 있다 ⇒ `projects.routes.ts:874` 「경로 이름은 형제와 맞추고 메서드만 POST」.
 *
 * ⚠ 서버가 이름을 ★ «다시» 본다(`:136`) — ⇒ 「화면만 믿으면 다른 길로 지워진다」.
 *   ⇒ 그러니 이 함수는 화면이 받은 글자를 ★ 그대로 보낸다(다듬지 않는다 — 다듬으면 서버 판정과 어긋난다).
 *
 * 오류 ⇒ 404 PROJECT_NOT_FOUND · 403 FORBIDDEN · 403 FORBIDDEN_NOT_CREATOR
 *        · 400 CONFIRM_NAME_MISMATCH  ⇐ ★ 400 은 «맨 뒤»다(`:919` — 먼저 재면 존재가 샌다)
 *   ⇒ ✅ 그래서 400 을 보는 사람은 ★ 이미 권한이 있다 ⇒ 「이름을 다시」로 그려도 정보가 안 샌다.
 */
export async function purgeProject(projectId: string, confirmName: string): Promise<void> {
  await apiClient.post(`/projects/${projectId}/purge`, { confirmName });
}
